import type { ProgramView } from "@/api/perkpoint/programs";
import type { ApiSegmentsOverview } from "@/api/perkpoint/insights";
import type {
  ApiCampaign,
  ApiCampaignChannel,
  ApiCampaignSchedule,
  ApiCampaignType,
  CreateCampaignPayload,
} from "@/api/perkpoint/campaigns";
import type { CampaignRow, CampaignStatus, CampaignType, Channel } from "../Campaigns";

/* ------------------------------------------------------------------ */
/* Draft shape                                                         */
/* ------------------------------------------------------------------ */

export type ChannelKey = "whatsapp" | "sms" | "email" | "inapp" | "web";
export type StepId = "basics" | "audience" | "channels" | "schedule" | "review";
export type ScheduleMode = "once" | "recurring" | "event";

export type CampaignDraft = {
  id?: string;
  code: string;
  name: string;
  description: string;
  type: CampaignType | "";
  offer: {
    enabled: boolean;
    kind: "multiply" | "fixed";
    multiplier: string;
    points: string;
    event: string;
    capPerMember: string;
    budgetPoints: string;
  };
  audience: {
    segments: string[];
    includeTiers: string[];
    excludeTiers: string[];
    controlPct: number;
  };
  channels: ChannelKey[];
  content: {
    whatsapp: {
      template: string;
      header: string;
      body: string;
      footer: string;
      buttonLabel: string;
      buttonUrl: string;
    };
    sms: { body: string };
    email: {
      fromName: string;
      subject: string;
      preheader: string;
      heading: string;
      body: string;
      ctaLabel: string;
      ctaUrl: string;
    };
    inapp: { title: string; body: string; ctaLabel: string };
    web: { headline: string; body: string; ctaLabel: string };
  };
  schedule: {
    mode: ScheduleMode;
    date: string;
    time: string;
    timeZone: string;
    recurrence: "daily" | "weekly" | "monthly";
    triggerEvent: string;
    delayHours: string;
    endDate: string;
    respectQuietHours: boolean;
    frequencyCap: string;
  };
};

export const STEPS: { id: StepId; label: string; hint: string }[] = [
  { id: "basics", label: "Basics", hint: "Name, type and reward" },
  { id: "audience", label: "Audience", hint: "Who receives it" },
  { id: "channels", label: "Channels", hint: "Message and preview" },
  { id: "schedule", label: "Schedule", hint: "When it goes out" },
  { id: "review", label: "Review", hint: "Check and launch" },
];

export const CAMPAIGN_TYPES: { value: CampaignType; label: string; hint: string }[] = [
  { value: "Promotional", label: "Promotional", hint: "Bonus points or a limited-time offer" },
  { value: "Lifecycle", label: "Lifecycle", hint: "Welcome, birthday, win-back" },
  { value: "Referral", label: "Referral", hint: "Reward members who bring friends" },
  { value: "Seasonal", label: "Seasonal", hint: "Festivals and holidays" },
  { value: "Tier", label: "Tier", hint: "Nudge members towards the next tier" },
];

export const CHANNELS: {
  key: ChannelKey;
  label: Channel;
  hint: string;
  consentRate: number;
}[] = [
  { key: "whatsapp", label: "WhatsApp", hint: "Approved template, highest read rate", consentRate: 0.62 },
  { key: "email", label: "Email", hint: "Rich layout with a call to action", consentRate: 0.71 },
  { key: "sms", label: "SMS", hint: "Short text, every phone", consentRate: 0.48 },
  { key: "inapp", label: "In-app", hint: "Card shown in the member app", consentRate: 0.55 },
  { key: "web", label: "Web", hint: "Banner on the member website", consentRate: 1 },
];

export const OFFER_EVENTS = [
  { value: "booking.completed", label: "Booking completed" },
  { value: "payment.captured", label: "Payment captured" },
  { value: "member.enrolled", label: "Member enrolled" },
];

export const TRIGGER_EVENTS = [
  { value: "member.enrolled", label: "Member enrols" },
  { value: "booking.completed", label: "Booking completed" },
  { value: "points.redeemed", label: "Points redeemed" },
  { value: "tier.upgraded", label: "Tier upgraded" },
  { value: "member.birthday", label: "Member birthday" },
];

/** Audience pick that means "every member"; never sent as a segment id. */
export const ALL_MEMBERS = "all";

export type SegmentOption = { id: string; label: string; members: number };
export type TierOption = { id: string; name: string; members: number };

export const VARIABLES = [
  { key: "first_name", label: "First name" },
  { key: "points_balance", label: "Points balance" },
  { key: "tier", label: "Tier" },
  { key: "bonus", label: "Bonus" },
  { key: "offer_end_date", label: "Offer end date" },
  { key: "program_name", label: "Program name" },
];

/* ------------------------------------------------------------------ */
/* Program context                                                     */
/* ------------------------------------------------------------------ */

export type ProgramContext = {
  name: string;
  pointsName: string;
  currency: string;
  costPerPoint: number;
  timeZone: string;
  quietHoursStart: string;
  quietHoursEnd: string;
  frequencyCap: number;
  approvalThreshold: number;
  brandColor: string;
  smsSender: string;
  fromEmail: string;
  whatsappNumber: string;
  tiers: TierOption[];
  segments: SegmentOption[];
  totalMembers: number;
};

function segmentOptions(overview?: ApiSegmentsOverview): SegmentOption[] {
  if (!overview) return [];
  const rfm = [...(overview.built_in ?? []), ...(overview.custom_segments ?? [])].map(
    (segment) => ({ id: segment.id, label: segment.label, members: segment.members ?? 0 }),
  );
  const conditional = (overview.other_segments ?? []).map((segment) => ({
    id: segment.id,
    label: segment.name,
    members: segment.member_count ?? 0,
  }));
  return [...rfm, ...conditional].filter((segment) => Boolean(segment.id));
}

/** Settings the builder needs from the program (defaults while it loads). */
export function programContext(
  program?: ProgramView,
  segments?: ApiSegmentsOverview,
): ProgramContext {
  return {
    name: program?.name ?? "Perk Point",
    pointsName: program?.loyaltyPointsName || "points",
    currency: program?.currency ?? "AED",
    costPerPoint: program?.costPerPoint ?? 0.01,
    timeZone: program?.timeZone ?? "Asia/Dubai",
    quietHoursStart: program?.quietHoursStart ?? "21:00",
    quietHoursEnd: program?.quietHoursEnd ?? "08:00",
    frequencyCap: program?.messageFrequencyCap ?? 3,
    approvalThreshold: program?.offerApprovalThreshold ?? 5000,
    brandColor: program?.brandPrimaryColor || "#1B7A45",
    smsSender: program?.messagingSmsSenderId || "PERKPT",
    fromEmail: program?.messagingFromEmail || "rewards@perkpoint.example",
    whatsappNumber: program?.messagingWhatsappNumber || "",
    tiers: (program?.tiers ?? []).map((tier) => ({
      id: tier.id,
      name: tier.name,
      members: tier.members ?? 0,
    })),
    segments: segmentOptions(segments),
    totalMembers: segments?.total_members ?? program?.membersCount ?? 0,
  };
}

/* ------------------------------------------------------------------ */
/* Defaults and conversions                                            */
/* ------------------------------------------------------------------ */

function isoDate(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function emptyDraft(ctx: ProgramContext): CampaignDraft {
  const start = new Date();
  start.setDate(start.getDate() + 3);
  const end = new Date(start);
  end.setDate(end.getDate() + 30);
  return {
    code: "",
    name: "",
    description: "",
    type: "",
    offer: {
      enabled: true,
      kind: "multiply",
      multiplier: "2",
      points: "500",
      event: "booking.completed",
      capPerMember: "2000",
      budgetPoints: "",
    },
    audience: { segments: [], includeTiers: [], excludeTiers: [], controlPct: 10 },
    channels: ["whatsapp", "email"],
    content: {
      whatsapp: {
        template: "",
        header: "",
        body: "Hi {{first_name}}, earn {{bonus}} on every booking until {{offer_end_date}}. You have {{points_balance}} to spend.",
        footer: "Reply STOP to opt out",
        buttonLabel: "Book now",
        buttonUrl: "",
      },
      sms: {
        body: "{{program_name}}: Hi {{first_name}}, earn {{bonus}} on bookings until {{offer_end_date}}. STOP to opt out",
      },
      email: {
        fromName: ctx.name,
        subject: "",
        preheader: "",
        heading: "",
        body: "Hi {{first_name}},\n\nFor a limited time you earn {{bonus}} on every booking. Your balance is {{points_balance}}.\n\nThe offer ends on {{offer_end_date}}.",
        ctaLabel: "Book now",
        ctaUrl: "",
      },
      inapp: {
        title: "",
        body: "Earn {{bonus}} on bookings until {{offer_end_date}}.",
        ctaLabel: "See offer",
      },
      web: {
        headline: "",
        body: "Members earn {{bonus}} until {{offer_end_date}}.",
        ctaLabel: "Learn more",
      },
    },
    schedule: {
      mode: "once",
      date: isoDate(start),
      time: "10:00",
      timeZone: ctx.timeZone,
      recurrence: "weekly",
      triggerEvent: "member.enrolled",
      delayHours: "0",
      endDate: isoDate(end),
      respectQuietHours: true,
      frequencyCap: String(ctx.frequencyCap),
    },
  };
}

const CHANNEL_BY_LABEL = Object.fromEntries(
  CHANNELS.map((channel) => [channel.label, channel.key]),
) as Record<Channel, ChannelKey>;

function parseRowDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : isoDate(date);
}

/** Prefill the builder from an existing list row (seed campaigns). */
export function draftFromRow(row: CampaignRow, ctx: ProgramContext): CampaignDraft {
  const base = emptyDraft(ctx);
  const multiply = row.bonusPoints.match(/([\d.]+)\s*x/i);
  const fixed = row.bonusPoints.match(/([\d,]+)\s*pts/i);
  return {
    ...base,
    id: row.id,
    code: row.code,
    name: row.name,
    description: row.description,
    type: row.type,
    offer: {
      ...base.offer,
      enabled: Boolean(multiply || fixed),
      kind: multiply ? "multiply" : "fixed",
      multiplier: multiply ? multiply[1] : base.offer.multiplier,
      points: fixed ? fixed[1].replace(/,/g, "") : base.offer.points,
    },
    channels: row.channels.map((label) => CHANNEL_BY_LABEL[label]).filter(Boolean),
    schedule: {
      ...base.schedule,
      date: parseRowDate(row.startDate) || base.schedule.date,
      endDate: parseRowDate(row.endDate) || base.schedule.endDate,
    },
  };
}

function formatRowDate(iso: string) {
  if (!iso) return "—";
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function bonusLabel(draft: CampaignDraft, ctx: ProgramContext) {
  if (!draft.offer.enabled) return "No points offer";
  if (draft.offer.kind === "multiply") return `${draft.offer.multiplier || "?"}x points`;
  const points = Number(draft.offer.points) || 0;
  return `${points.toLocaleString("en-IN")} ${ctx.pointsName === "points" ? "pts" : ctx.pointsName}`;
}

/* ------------------------------------------------------------------ */
/* Live estimates                                                      */
/* ------------------------------------------------------------------ */

export type AudienceEstimate = {
  matched: number;
  tierExcluded: number;
  control: number;
  audience: number;
  reachable: number;
  byChannel: { key: ChannelKey; label: Channel; members: number }[];
};

export function isAllMembers(draft: CampaignDraft) {
  return (
    draft.audience.segments.length === 0 ||
    draft.audience.segments.includes(ALL_MEMBERS)
  );
}

export function estimateAudience(draft: CampaignDraft, ctx: ProgramContext): AudienceEstimate {
  const total = ctx.totalMembers;
  const picked = ctx.segments.filter((segment) => draft.audience.segments.includes(segment.id));
  let matched = isAllMembers(draft)
    ? total
    : picked.reduce((sum, segment) => sum + segment.members, 0);
  matched = Math.min(matched, total);

  const tierMembers = (ids: string[]) =>
    ctx.tiers
      .filter((tier) => ids.includes(tier.id))
      .reduce((sum, tier) => sum + tier.members, 0);
  if (draft.audience.includeTiers.length > 0) {
    const share = total ? tierMembers(draft.audience.includeTiers) / total : 0;
    matched = Math.round(matched * share);
  }
  const excludedShare = total ? tierMembers(draft.audience.excludeTiers) / total : 0;
  const tierExcluded = Math.round(matched * excludedShare);
  const afterTiers = matched - tierExcluded;
  const control = Math.round(afterTiers * (draft.audience.controlPct / 100));
  const audience = afterTiers - control;

  const byChannel = CHANNELS.filter((channel) => draft.channels.includes(channel.key)).map(
    (channel) => ({
      key: channel.key,
      label: channel.label,
      members: Math.round(audience * channel.consentRate),
    }),
  );
  const unreachable = CHANNELS.filter((c) => draft.channels.includes(c.key)).reduce(
    (product, channel) => product * (1 - channel.consentRate),
    1,
  );
  const reachable = draft.channels.length
    ? Math.round(audience * (1 - unreachable))
    : 0;

  return { matched, tierExcluded, control, audience, reachable, byChannel };
}

export function estimateCost(draft: CampaignDraft, audience: number, ctx: ProgramContext) {
  if (!draft.offer.enabled) return { points: 0, money: 0, needsApproval: false };
  const budget = Number(draft.offer.budgetPoints) || 0;
  const cap = Number(draft.offer.capPerMember) || 0;
  const fixed = draft.offer.kind === "fixed" ? Number(draft.offer.points) || 0 : 0;
  const perMember = draft.offer.kind === "fixed" ? (cap ? Math.min(cap, fixed) : fixed) : cap;
  const projected = perMember * audience;
  const points = budget > 0 ? Math.min(budget, projected || budget) : projected;
  return {
    points,
    money: points * ctx.costPerPoint,
    needsApproval: points >= ctx.approvalThreshold,
  };
}

/* ------------------------------------------------------------------ */
/* Message helpers                                                     */
/* ------------------------------------------------------------------ */

export function sampleValues(draft: CampaignDraft, ctx: ProgramContext): Record<string, string> {
  const end = draft.schedule.endDate
    ? new Date(`${draft.schedule.endDate}T00:00:00`).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : "31 Dec";
  return {
    first_name: "Layla",
    points_balance: `8,420 ${ctx.pointsName}`,
    tier: "Gold",
    bonus: draft.offer.enabled
      ? draft.offer.kind === "multiply"
        ? `${draft.offer.multiplier || "2"}x ${ctx.pointsName}`
        : `${Number(draft.offer.points || 0).toLocaleString("en-IN")} bonus ${ctx.pointsName}`
      : `${ctx.pointsName}`,
    offer_end_date: end,
    program_name: ctx.name,
  };
}

export function fillVariables(text: string, values: Record<string, string>) {
  return text.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key: string) =>
    values[key] ?? match,
  );
}

/** SMS length after variables. GSM-7 = 160/153, otherwise UCS-2 = 70/67. */
export function smsSegments(text: string) {
  // eslint-disable-next-line no-control-regex
  const gsm = /^[\x0A\x0D\x20-\x7E£¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ¤¡ÄÖÑÜ§¿äöñüà€]*$/.test(text);
  const single = gsm ? 160 : 70;
  const multi = gsm ? 153 : 67;
  const length = text.length;
  const segments = length === 0 ? 0 : length <= single ? 1 : Math.ceil(length / multi);
  return { length, segments, unicode: !gsm, limit: segments <= 1 ? single : multi * segments };
}

function inQuietHours(time: string, start: string, end: string) {
  if (!time) return false;
  return start <= end ? time >= start && time < end : time >= start || time < end;
}

/* ------------------------------------------------------------------ */
/* Validation                                                          */
/* ------------------------------------------------------------------ */

export function stepErrors(step: StepId, draft: CampaignDraft, ctx: ProgramContext): string[] {
  const errors: string[] = [];
  if (step === "basics") {
    if (!draft.name.trim()) errors.push("Give the campaign a name.");
    if (draft.name.trim().length > 200) errors.push("Keep the name within 200 characters.");
    if (draft.description.trim().length > 2000) {
      errors.push("Keep the description within 2000 characters.");
    }
    if (!draft.type) errors.push("Pick a campaign type.");
    if (draft.offer.enabled) {
      const multiplier = Number(draft.offer.multiplier);
      if (draft.offer.kind === "multiply" && !(multiplier > 1)) {
        errors.push("The multiplier must be more than 1x.");
      }
      if (draft.offer.kind === "multiply" && multiplier > 10) {
        errors.push("The multiplier can be at most 10x.");
      }
      if (
        draft.offer.kind === "fixed" &&
        !(Number(draft.offer.points) > 0 && Number.isInteger(Number(draft.offer.points)))
      ) {
        errors.push("Enter a whole number of bonus points to give.");
      }
    }
  }
  if (step === "audience") {
    if (ctx.totalMembers > 0 && estimateAudience(draft, ctx).audience <= 0) {
      errors.push("The audience is empty. Widen the segments or tiers.");
    }
  }
  if (step === "channels") {
    const c = draft.content;
    if (draft.channels.length === 0) errors.push("Pick at least one channel.");
    if (draft.channels.includes("whatsapp")) {
      if (!c.whatsapp.template.trim()) errors.push("WhatsApp: enter the approved template name.");
      if (!c.whatsapp.body.trim()) errors.push("WhatsApp: the message is empty.");
    }
    if (draft.channels.includes("sms")) {
      const filled = fillVariables(c.sms.body, sampleValues(draft, ctx));
      if (!c.sms.body.trim()) errors.push("SMS: the message is empty.");
      else if (smsSegments(filled).segments > 3) errors.push("SMS: keep it within 3 parts.");
    }
    if (draft.channels.includes("email")) {
      if (!c.email.subject.trim()) errors.push("Email: add a subject line.");
      if (!c.email.body.trim()) errors.push("Email: the body is empty.");
    }
    if (draft.channels.includes("inapp")) {
      if (!c.inapp.title.trim()) errors.push("In-app: add a title.");
    }
    if (draft.channels.includes("web")) {
      if (!c.web.headline.trim()) errors.push("Web: add a headline.");
    }
  }
  if (step === "schedule") {
    const s = draft.schedule;
    if (s.mode !== "event") {
      if (!s.date) errors.push("Pick a start date.");
      if (!s.time) errors.push("Pick a send time.");
      const start = new Date(`${s.date}T${s.time || "00:00"}`);
      if (s.date && start.getTime() < Date.now()) errors.push("The start time is in the past.");
    }
    if (s.mode === "event" && !s.triggerEvent) errors.push("Pick the event that triggers the message.");
    if (s.endDate && s.date && s.endDate < s.date) errors.push("The end date is before the start date.");
    if (
      s.mode !== "event" &&
      s.respectQuietHours &&
      inQuietHours(s.time, ctx.quietHoursStart, ctx.quietHoursEnd)
    ) {
      errors.push(`The send time falls in quiet hours (${ctx.quietHoursStart}–${ctx.quietHoursEnd}).`);
    }
  }
  return errors;
}

export type ReviewCheck = { ok: boolean; warn?: boolean; message: string };

export function reviewChecks(draft: CampaignDraft, ctx: ProgramContext): ReviewCheck[] {
  const audience = estimateAudience(draft, ctx);
  const cost = estimateCost(draft, audience.audience, ctx);
  const checks: ReviewCheck[] = STEPS.filter((s) => s.id !== "review").map((step) => {
    const errors = stepErrors(step.id, draft, ctx);
    return errors.length
      ? { ok: false, message: `${step.label}: ${errors[0]}` }
      : { ok: true, message: `${step.label} is complete.` };
  });
  if (draft.channels.includes("email")) {
    checks.push({ ok: true, message: "Email gets an unsubscribe link automatically." });
  }
  checks.push({
    ok: true,
    message: `Members get at most ${draft.schedule.frequencyCap || ctx.frequencyCap} messages a day from this program.`,
  });
  if (cost.needsApproval) {
    checks.push({
      ok: true,
      warn: true,
      message: `Points cost is at or above ${ctx.approvalThreshold.toLocaleString("en-IN")}, so a second admin must approve.`,
    });
  }
  return checks;
}

/* ------------------------------------------------------------------ */
/* API payload                                                         */
/* ------------------------------------------------------------------ */

function positiveNumber(value: string): number | undefined {
  const n = Number(value);
  return value.trim() && Number.isFinite(n) && n > 0 ? n : undefined;
}

function optionalText(value: string): string | undefined {
  const text = value.trim();
  return text ? text : undefined;
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** The email body as HTML, so the heading and button reach the API too. */
function emailHtml(email: CampaignDraft["content"]["email"]): string | undefined {
  const parts: string[] = [];
  if (email.heading.trim()) parts.push(`<h1>${escapeHtml(email.heading.trim())}</h1>`);
  email.body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .forEach((paragraph) =>
      parts.push(`<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`),
    );
  if (email.ctaLabel.trim() && email.ctaUrl.trim()) {
    parts.push(
      `<p><a href="${escapeHtml(email.ctaUrl.trim())}">${escapeHtml(email.ctaLabel.trim())}</a></p>`,
    );
  }
  return parts.length ? parts.join("") : undefined;
}

function channelPayload(key: ChannelKey, content: CampaignDraft["content"]): ApiCampaignChannel {
  switch (key) {
    case "whatsapp":
      return {
        channel: "whatsapp",
        template_name: content.whatsapp.template.trim(),
        header: optionalText(content.whatsapp.header),
        body: content.whatsapp.body,
        footer: optionalText(content.whatsapp.footer),
        button_label: optionalText(content.whatsapp.buttonLabel),
        button_link: optionalText(content.whatsapp.buttonUrl),
      };
    case "email":
      return {
        channel: "email",
        subject: content.email.subject.trim(),
        body: content.email.body,
        html_body: emailHtml(content.email),
      };
    case "sms":
      return { channel: "sms", body: content.sms.body };
    case "inapp":
      return { channel: "in_app", title: content.inapp.title.trim(), body: content.inapp.body };
    case "web":
      return { channel: "web", title: content.web.headline.trim(), body: content.web.body };
  }
}

function schedulePayload(schedule: CampaignDraft["schedule"]): ApiCampaignSchedule {
  const common = {
    time_zone: schedule.timeZone,
    ends_at: optionalText(schedule.endDate),
    respect_quiet_hours: schedule.respectQuietHours,
    max_messages_per_day: positiveNumber(schedule.frequencyCap),
  };
  if (schedule.mode === "event") {
    return {
      mode: "event",
      event_type_key: schedule.triggerEvent,
      delay_minutes: Math.round((Number(schedule.delayHours) || 0) * 60),
      ...common,
    };
  }
  if (schedule.mode === "recurring") {
    const first = new Date(`${schedule.date}T00:00:00`);
    const valid = !Number.isNaN(first.getTime());
    return {
      mode: "repeating",
      repeat: {
        frequency: schedule.recurrence,
        time: schedule.time,
        day_of_week: schedule.recurrence === "weekly" && valid ? first.getDay() : undefined,
        day_of_month: schedule.recurrence === "monthly" && valid ? first.getDate() : undefined,
      },
      ...common,
    };
  }
  return { mode: "once", send_at: `${schedule.date}T${schedule.time}`, ...common };
}

const TYPE_FROM_API: Record<string, CampaignType> = {
  promotional: "Promotional",
  lifecycle: "Lifecycle",
  referral: "Referral",
  seasonal: "Seasonal",
  tier: "Tier",
};

const CHANNEL_KEY_FROM_API: Record<string, ChannelKey> = {
  whatsapp: "whatsapp",
  email: "email",
  sms: "sms",
  in_app: "inapp",
  inapp: "inapp",
  web: "web",
};

function splitDateTime(value: string | null | undefined): { date: string; time: string } {
  if (!value) return { date: "", time: "" };
  // 2026-11-01T10:00 or full ISO
  const match = value.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/,
  );
  if (match) return { date: match[1], time: match[2] };
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: "10:00" };
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: "", time: "" };
  return {
    date: isoDate(parsed),
    time: `${String(parsed.getHours()).padStart(2, "0")}:${String(parsed.getMinutes()).padStart(2, "0")}`,
  };
}

/** Prefill the create/edit wizard from GET campaign detail. */
export function draftFromApiCampaign(
  api: ApiCampaign,
  ctx: ProgramContext,
): CampaignDraft {
  const base = emptyDraft(ctx);
  const typeKey = (api.campaign_type || "").toLowerCase();
  const reward = api.reward;
  const audience = api.audience;
  const schedule = api.schedule as
    | {
        mode?: string;
        send_at?: string;
        ends_at?: string;
        time_zone?: string;
        respect_quiet_hours?: boolean;
        max_messages_per_day?: number;
        event_type_key?: string;
        delay_minutes?: number;
        repeat?: {
          frequency?: string;
          time?: string;
          day_of_week?: number;
          day_of_month?: number;
        };
      }
    | null
    | undefined;

  const channelKeys: ChannelKey[] = [];
  const content = structuredClone(base.content);
  for (const item of api.channels ?? []) {
    if (typeof item === "string") {
      const key = CHANNEL_KEY_FROM_API[item.toLowerCase()];
      if (key && !channelKeys.includes(key)) channelKeys.push(key);
      continue;
    }
    const ch = item as {
      channel?: string;
      template_name?: string | null;
      subject?: string | null;
      title?: string | null;
      header?: string | null;
      body?: string | null;
      footer?: string | null;
      button_label?: string | null;
      button_link?: string | null;
    };
    const key = CHANNEL_KEY_FROM_API[(ch.channel || "").toLowerCase()];
    if (!key) continue;
    if (!channelKeys.includes(key)) channelKeys.push(key);
    if (key === "whatsapp") {
      content.whatsapp = {
        template: ch.template_name || "",
        header: ch.header || "",
        body: ch.body || content.whatsapp.body,
        footer: ch.footer || "",
        buttonLabel: ch.button_label || "",
        buttonUrl: ch.button_link || "",
      };
    } else if (key === "email") {
      content.email = {
        ...content.email,
        subject: ch.subject || "",
        body: ch.body || content.email.body,
      };
    } else if (key === "sms") {
      content.sms = { body: ch.body || content.sms.body };
    } else if (key === "inapp") {
      content.inapp = {
        title: ch.title || "",
        body: ch.body || content.inapp.body,
        ctaLabel: content.inapp.ctaLabel,
      };
    } else if (key === "web") {
      content.web = {
        headline: ch.title || "",
        body: ch.body || content.web.body,
        ctaLabel: content.web.ctaLabel,
      };
    }
  }

  const nestedSegmentIds =
    audience?.segments
      ?.map((s) => s.id)
      .filter((id): id is string => Boolean(id)) ?? [];
  const resolvedSegmentIds =
    audience?.segment_ids != null ? audience.segment_ids : nestedSegmentIds;
  const nestedIncludeTier =
    audience?.include_tiers
      ?.map((t) => t.id)
      .filter((id): id is string => Boolean(id)) ?? [];
  const nestedExcludeTier =
    audience?.exclude_tiers
      ?.map((t) => t.id)
      .filter((id): id is string => Boolean(id)) ?? [];
  const resolvedIncludeTiers =
    audience?.include_tier_ids != null
      ? audience.include_tier_ids
      : nestedIncludeTier;
  const resolvedExcludeTiers =
    audience?.exclude_tier_ids != null
      ? audience.exclude_tier_ids
      : nestedExcludeTiers;
  const allMembers =
    audience?.all_members === true ||
    (audience?.all_members !== false && !resolvedSegmentIds.length);
  const sendAt =
    schedule?.send_at ||
    api.scheduled_at ||
    api.starts_at ||
    "";
  const { date, time } = splitDateTime(sendAt);
  const endRaw = schedule?.ends_at || api.ends_at || "";
  const endDate = /^\d{4}-\d{2}-\d{2}/.test(endRaw)
    ? endRaw.slice(0, 10)
    : splitDateTime(endRaw).date;

  let mode: ScheduleMode = "once";
  if (schedule?.mode === "repeating" || schedule?.mode === "recurring") mode = "recurring";
  else if (schedule?.mode === "event" || api.trigger_type === "event_triggered") {
    mode = "event";
  }

  const recurrence =
    schedule?.repeat?.frequency === "daily" ||
    schedule?.repeat?.frequency === "monthly"
      ? schedule.repeat.frequency
      : "weekly";

  return {
    ...base,
    id: api.id,
    code: api.display_code?.trim() || "",
    name: api.name || "",
    description: api.description?.trim() || "",
    type: TYPE_FROM_API[typeKey] ?? "",
    offer: {
      enabled: Boolean(reward && reward.enabled !== false && reward.type),
      kind: reward?.type === "fixed" ? "fixed" : "multiply",
      multiplier:
        reward?.multiplier != null ? String(reward.multiplier) : base.offer.multiplier,
      points: reward?.points != null ? String(reward.points) : base.offer.points,
      event: reward?.when || api.goal_event_type_key || base.offer.event,
      capPerMember:
        reward?.cap_per_member != null
          ? String(reward.cap_per_member)
          : base.offer.capPerMember,
      budgetPoints:
        reward && "total_budget" in reward && reward.total_budget != null
          ? String(reward.total_budget)
          : api.budget_points != null
            ? String(api.budget_points)
            : "",
    },
    audience: {
      segments: allMembers ? [ALL_MEMBERS] : [...resolvedSegmentIds],
      includeTiers: [...resolvedIncludeTiers],
      excludeTiers: [...resolvedExcludeTiers],
      controlPct: Number(
        audience?.control_group_pct ?? api.control_group_pct ?? 10,
      ) || 0,
    },
    channels: channelKeys.length ? channelKeys : [],
    content,
    schedule: {
      mode,
      date: date || base.schedule.date,
      time: schedule?.repeat?.time || time || base.schedule.time,
      timeZone: schedule?.time_zone || api.time_zone || ctx.timeZone,
      recurrence,
      triggerEvent:
        schedule?.event_type_key ||
        api.trigger_event_type_key ||
        base.schedule.triggerEvent,
      delayHours:
        schedule?.delay_minutes != null
          ? String(schedule.delay_minutes / 60)
          : api.delay_seconds != null
            ? String(api.delay_seconds / 3600)
            : "0",
      endDate: endDate || base.schedule.endDate,
      respectQuietHours: schedule?.respect_quiet_hours ?? true,
      frequencyCap:
        schedule?.max_messages_per_day != null
          ? String(schedule.max_messages_per_day)
          : String(ctx.frequencyCap),
    },
  };
}

/**
 * Body for POST/PATCH /admin/programs/:programId/campaigns.
 * With `onlyCompleteSteps`, sections whose step still has errors are left out,
 * so a half-built draft can be saved (the API only requires `name`).
 */
export function draftToCampaignPayload(
  draft: CampaignDraft,
  ctx: ProgramContext,
  options: { onlyCompleteSteps?: boolean } = {},
): CreateCampaignPayload {
  const include = (step: StepId) =>
    !options.onlyCompleteSteps || stepErrors(step, draft, ctx).length === 0;
  const offer = draft.offer;

  const payload: CreateCampaignPayload = {
    name: draft.name.trim(),
    description: optionalText(draft.description),
    campaign_type: draft.type ? (draft.type.toLowerCase() as ApiCampaignType) : undefined,
  };

  if (include("basics")) {
    payload.reward = offer.enabled
      ? {
          enabled: true,
          type: offer.kind,
          multiplier: offer.kind === "multiply" ? Number(offer.multiplier) : undefined,
          points: offer.kind === "fixed" ? Number(offer.points) : undefined,
          when: offer.event,
          cap_per_member: positiveNumber(offer.capPerMember),
          total_budget: positiveNumber(offer.budgetPoints),
        }
      : { enabled: false };
  }

  if (include("audience")) {
    const allMembers = isAllMembers(draft);
    payload.audience = {
      all_members: allMembers,
      segment_ids: allMembers
        ? []
        : draft.audience.segments.filter((id) => id !== ALL_MEMBERS),
      include_tier_ids: draft.audience.includeTiers,
      exclude_tier_ids: draft.audience.excludeTiers,
      control_group_pct: draft.audience.controlPct,
    };
  }

  if (include("channels") && draft.channels.length) {
    payload.channels = CHANNELS.filter((channel) => draft.channels.includes(channel.key)).map(
      (channel) => channelPayload(channel.key, draft.content),
    );
  }

  if (include("schedule")) {
    payload.schedule = schedulePayload(draft.schedule);
  }

  return payload;
}

/* ------------------------------------------------------------------ */
/* In-memory store (until the campaigns list API is wired)             */
/* ------------------------------------------------------------------ */

const savedDrafts = new Map<string, { draft: CampaignDraft; row: CampaignRow }>();

export function getSavedCampaignRows(): CampaignRow[] {
  return [...savedDrafts.values()].map((item) => item.row).reverse();
}

export function getSavedDraft(id: string): CampaignDraft | undefined {
  const saved = savedDrafts.get(id);
  return saved ? structuredClone(saved.draft) : undefined;
}

/** Next free campaign ID, e.g. CP-008. The backend should assign this. */
export function nextCampaignCode(existingCodes: string[]): string {
  const next =
    existingCodes.reduce((max, code) => {
      const n = Number(code.replace(/\D/g, ""));
      return Number.isFinite(n) ? Math.max(max, n) : max;
    }, 0) + 1;
  return `CP-${String(next).padStart(3, "0")}`;
}

export function saveCampaign(
  draft: CampaignDraft,
  status: CampaignStatus,
  ctx: ProgramContext,
  existingCodes: string[],
  previous?: CampaignRow,
): CampaignRow {
  const id = draft.id ?? `cmp-${Date.now()}`;
  const code = draft.code.trim() || nextCampaignCode(existingCodes);
  const row: CampaignRow = {
    id,
    code,
    name: draft.name.trim(),
    description: draft.description.trim(),
    type: (draft.type || "Promotional") as CampaignType,
    channels: CHANNELS.filter((c) => draft.channels.includes(c.key)).map((c) => c.label),
    bonusPoints: bonusLabel(draft, ctx),
    startDate: draft.schedule.mode === "event" ? "On event" : formatRowDate(draft.schedule.date),
    endDate: formatRowDate(draft.schedule.endDate),
    status,
    membersReached: previous?.membersReached ?? "—",
    pointsIssued: previous?.pointsIssued ?? "0",
    messages: previous?.messages ?? "0",
  };
  savedDrafts.set(id, { draft: { ...structuredClone(draft), id, code }, row });
  return row;
}
